<template>
  <div>
    <div class="sync-sidebar-overlay" v-if="isOpen" @click="closeSidebar">
      <div class="sync-sidebar" @click.stop>
        <div class="sidebar-content">

          <!-- Header -->
          <div class="sidebar-header">
            <h2>Synchronisation</h2>
            <button @click="closeSidebar" class="close-btn">
              <i class="ico close" />
            </button>
          </div>

          <!-- Statut API -->
          <div class="sync-status">
            <div class="status-indicator" :class="{ 'online': isApiAvailable, 'offline': !isApiAvailable }">
              <div class="status-icon"></div>
              <span>{{ isApiAvailable ? 'API Accessible' : 'API Non Accessible' }}</span>
            </div>
          </div>

          <!-- Sync en cours -->
          <div v-if="isSyncingData" class="incremental-sync-progress">
            <div class="sync-in-progress-header">
              <i class="fas fa-spinner fa-spin"></i>
              <span v-if="isExportSync">Téléchargement de l'export en cours…</span>
              <span v-else>Synchronisation des données en cours…</span>
            </div>
            <div class="sync-progress-detail">
              <span class="sync-entity-label" v-if="syncEntityLabel">{{ syncEntityLabel }}</span>
              <span v-if="isExportSync" class="sync-export-hint">
                <i class="fas fa-file-archive"></i>
                Décompression et enregistrement en cours…
              </span>
              <template v-else>
                <span class="sync-page-info" v-if="syncProgress.totalPages > 0">
                  Page {{ syncProgress.currentPage }} / {{ syncProgress.totalPages }}
                </span>
                <span class="sync-fetched-info" v-if="syncProgress.totalFetched > 0">
                  {{ syncProgress.totalFetched }} enregistrement{{ syncProgress.totalFetched > 1 ? 's' : '' }} récupéré{{ syncProgress.totalFetched > 1 ? 's' : '' }}
                </span>
              </template>
            </div>
          </div>

          <!-- ① Modifications locales -->
          <div class="actions-section">
            <div class="actions-section-title">
              <span class="section-number">①</span>
              <span>Modifications locales en attente</span>
              <span v-if="totalPending > 0" class="pending-badge-inline">{{ totalPending }}</span>
            </div>

            <div class="pending-stats" v-if="totalPending > 0">
              <div class="stat-item" v-if="pendingMutations.length > 0">
                <span class="stat-label">
                  <i class="fas fa-cloud-upload-alt"></i> Mutations :
                </span>
                <span class="stat-value">{{ pendingMutations.length }}</span>
              </div>
              <div class="stat-item" v-if="syncStats.propertiesWithToSync > 0">
                <span class="stat-label">
                  <i class="fas fa-building"></i> Propriétés :
                </span>
                <span class="stat-value">{{ syncStats.propertiesWithToSync }}</span>
              </div>
            </div>
            <div class="no-pending" v-else>
              <i class="fas fa-check-circle"></i>
              Aucune modification locale en attente
            </div>

            <button
              @click="forceSyncNow"
              :disabled="!isApiAvailable || isSyncing || isSyncingData"
              class="action-btn primary"
            >
              <i class="fas" :class="isSyncing ? 'fa-spinner fa-spin' : 'fa-cloud-upload-alt'" />
              <span>{{ isSyncing ? 'Envoi en cours…' : 'Envoyer les modifications au serveur' }}</span>
            </button>
            <p class="action-description">
              Synchronise les planifications de visites, attributions de gestionnaires et visites créées hors connexion.
            </p>
          </div>

          <!-- ② Données reçues du serveur -->
          <div class="actions-section">
            <div class="actions-section-title">
              <span class="section-number">②</span>
              <span>Données reçues du serveur</span>
            </div>

            <!-- Tableau d'état de sync par entité -->
            <div class="sync-info-grid" v-if="localSyncInfo.length > 0">
              <div class="sync-info-row sync-info-header">
                <span>Entité</span>
                <span>Dernière sync</span>
                <span title="Date de génération de l'export ZIP utilisé pour le chargement initial">Export ZIP</span>
              </div>
              <div
                v-for="info in localSyncInfo"
                :key="info.entity"
                class="sync-info-row"
                :class="'sync-status-' + info.status"
              >
                <span class="info-entity">{{ entityLabels[info.entity] || info.entity }}</span>
                <span class="info-date" :title="info.lastSyncDate || 'Jamais'">
                  {{ formatDateShort(info.lastSyncDate) }}
                </span>
                <span class="info-date" :title="info.exportGenerationDate || 'Inconnu'">
                  {{ formatDateShort(info.exportGenerationDate) }}
                </span>
              </div>
            </div>

            <button
              @click="launchSmartSync"
              :disabled="!isApiAvailable || isSyncingData || isSmartSyncing"
              class="action-btn secondary"
            >
              <i class="fas" :class="isSmartSyncing ? 'fa-spinner fa-spin' : 'fa-sync-alt'" />
              <span>{{ isSmartSyncing ? 'Synchronisation en cours…' : 'Synchroniser maintenant' }}</span>
            </button>
            <p class="action-description">
              Vérifie immédiatement les nouvelles données du serveur et les télécharge (synchronisation incrémentale ou export ZIP selon le volume).
            </p>

            <button
              @click="showResyncModal = true"
              :disabled="!isApiAvailable || isSyncingData || isSmartSyncing"
              class="action-btn ghost"
              title="Resynchronise les propriétés depuis une date passée pour récupérer les lots manquants"
            >
              <i class="fas fa-calendar-alt" :class="{ 'fa-spin': isFullResyncing }" />
              <span>{{ isFullResyncing ? 'Resynchronisation…' : 'Resynchroniser les propriétés depuis une date…' }}</span>
            </button>
            <p class="action-description">
              Récupère toutes les propriétés modifiées depuis une date choisie. Utile si des lots semblent manquants dans la PWA suite à un import tardif en base.
            </p>
          </div>

          <!-- ③ Re-téléchargement ZIP forcé -->
          <div class="actions-section zip-section">
            <div class="actions-section-title">
              <span class="section-number">③</span>
              <span>Forcer le re-téléchargement ZIP</span>
            </div>
            <p class="section-description">
              À utiliser si un téléchargement ZIP a été interrompu (rafraîchissement ou coupure réseau) ou si les données semblent incomplètes.
              L'entrée de synchronisation est effacée et le fichier ZIP est re-téléchargé intégralement.
            </p>

            <div class="zip-buttons">
              <button
                @click="forceZipDownload('properties')"
                :disabled="!isApiAvailable || isSyncingData || zipDownloading.properties"
                class="action-btn zip-btn"
              >
                <i class="fas" :class="zipDownloading.properties ? 'fa-spinner fa-spin' : 'fa-file-archive'" />
                <span>{{ zipDownloading.properties ? 'Téléchargement en cours…' : 'Re-télécharger l\'export Propriétés' }}</span>
              </button>
              <p class="action-description">
                Re-télécharge l'intégralité des propriétés depuis le fichier ZIP serveur (~plusieurs Mo, quelques secondes).
              </p>

              <button
                @click="forceZipDownload('controls')"
                :disabled="!isApiAvailable || isSyncingData || zipDownloading.controls"
                class="action-btn zip-btn"
              >
                <i class="fas" :class="zipDownloading.controls ? 'fa-spinner fa-spin' : 'fa-file-archive'" />
                <span>{{ zipDownloading.controls ? 'Téléchargement en cours…' : 'Re-télécharger l\'export Contrôles' }}</span>
              </button>
              <p class="action-description">
                Re-télécharge l'intégralité des points de contrôle depuis le fichier ZIP serveur.
              </p>
            </div>
          </div>

          <!-- Résultat de la dernière action -->
          <div class="sync-history" v-if="lastSyncResult">
            <h3>Résultat — Dernière action</h3>
            <div class="history-item" :class="{ 'success': lastSyncResult.success, 'error': !lastSyncResult.success }">
              <div class="history-icon">
                <i :class="lastSyncResult.success ? 'ico check' : 'ico error'" />
              </div>
              <div class="history-details">
                <div class="history-message">
                  {{ lastSyncResult.success ? 'Réussie' : 'Échouée' }}
                </div>
                <div class="history-stats" v-if="lastSyncResult.syncedCount !== undefined">
                  {{ lastSyncResult.syncedCount }}/{{ lastSyncResult.totalCount }} synchronisées
                </div>
                <div class="history-time">{{ formatDate(lastSyncResult.timestamp) }}</div>
              </div>
            </div>
          </div>

          <!-- Tables IndexedDB (accordéon) -->
          <div class="sidebar-section">
            <div class="section-header accordion-header" @click="isIndexedDBSectionOpen = !isIndexedDBSectionOpen">
              <div class="header-left">
                <i class="ico" :class="isIndexedDBSectionOpen ? 'chevron-down' : 'chevron-right'" />
                <h3>Tables IndexedDB</h3>
              </div>
            </div>

            <div v-show="isIndexedDBSectionOpen" class="accordion-content">
              <div class="tables-list">
                <div
                  v-for="table in dbTables"
                  :key="table.name"
                  class="table-item"
                >
                  <div class="table-info">
                    <span class="table-name">{{ table.name }}</span>
                    <span class="table-count">{{ table.count }} {{ table.count > 1 ? 'enregistrements' : 'enregistrement' }}</span>
                  </div>
                  <div class="table-actions">
                    <button
                      v-if="table.name === 'mutations'"
                      @click="exportMutations"
                      class="export-btn"
                      title="Exporter en JSON"
                    >
                      <i class="ico download" />
                      Exporter
                    </button>
                    <button
                      @click="clearTable(table.name)"
                      class="clear-btn"
                      :disabled="table.count === 0 || clearingTables[table.name]"
                      title="Vider la table"
                    >
                      <i class="ico" :class="clearingTables[table.name] ? 'sync spinning' : 'trash'" />
                      {{ clearingTables[table.name] ? 'Vidage...' : 'Vider' }}
                    </button>
                  </div>
                </div>
              </div>
              <div class="clear-all-section">
                <button @click="showClearAllModal = true" class="clear-all-btn">
                  <i class="ico warning" />
                  Tout supprimer
                </button>
              </div>
            </div>
          </div>

          <!-- Liste détaillée des mutations en attente -->
          <div class="mutations-list" v-if="pendingMutations.length > 0">
            <h3>Détails des mutations</h3>
            <div class="mutations-scroll">
              <div
                v-for="mutation in pendingMutations"
                :key="mutation.id"
                class="mutation-item"
              >
                <div class="mutation-header">
                  <span class="mutation-entity">{{ getEntityLabel(mutation.entity) }}</span>
                  <span class="mutation-cloud-icon">☁</span>
                  <span class="mutation-type-badge">{{ getMutationTypeLabel(mutation.type) }}</span>
                  <span class="mutation-attempts" v-if="mutation.attempts > 0">
                    {{ mutation.attempts }} tentative{{ mutation.attempts > 1 ? 's' : '' }}
                  </span>
                </div>
                <div class="mutation-summary">
                  <div class="summary-item">
                    <strong>{{ getMutationSummary(mutation) }}</strong>
                  </div>
                </div>
                <div class="mutation-details">
                  <div class="mutation-info" v-if="mutation.entityId">
                    <span class="info-label">Lot/Entité ID:</span>
                    <span class="info-value">{{ mutation.entityId }}</span>
                  </div>
                  <div class="mutation-info">
                    <span class="info-label">Type opération:</span>
                    <span class="info-value">{{ getMutationTypeLabel(mutation.type) }}</span>
                  </div>
                  <div class="mutation-info" v-if="mutation.lastAttempt">
                    <span class="info-label">Dernière tentative:</span>
                    <span class="info-value">{{ formatDate(mutation.lastAttempt) }}</span>
                  </div>
                  <div class="mutation-info">
                    <span class="info-label">Créée:</span>
                    <span class="info-value">{{ formatDate(mutation.insertedAt) }}</span>
                  </div>
                </div>
                <div class="mutation-error" v-if="mutation.lastError">
                  <i class="ico warning" />
                  {{ mutation.lastError }}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Modal de resynchronisation des propriétés depuis une date -->
    <div v-if="showResyncModal" class="modal-overlay" @click="showResyncModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Resynchroniser les propriétés</h3>
          <button @click="showResyncModal = false" class="modal-close">
            <i class="ico close" />
          </button>
        </div>
        <div class="modal-body">
          <p>
            Cette action va re-télécharger <strong>toutes les propriétés modifiées depuis la date choisie</strong>
            et les ajouter/mettre à jour dans la base locale.
          </p>
          <p class="warning-text">
            Utile si des lots semblent manquants dans la PWA : certains lots peuvent avoir été
            ajoutés en base après la génération du dernier export et jamais synchronisés.
          </p>
          <div class="form-group" style="margin-top: 1rem;">
            <label for="resync-date" style="display:block; margin-bottom: 0.4rem; font-weight: 600;">
              Resynchroniser depuis le :
            </label>
            <input
              id="resync-date"
              type="date"
              v-model="resyncFromDate"
              style="width:100%; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 0.375rem;"
            />
            <p style="margin-top: 0.4rem; font-size: 0.8rem; color: #6b7280;">
              Choisissez une date antérieure aux lots manquants (ex : quelques jours avant
              leur date d'import en base).
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showResyncModal = false" class="btn-cancel">
            Annuler
          </button>
          <button @click="confirmResync" class="btn-confirm" :disabled="isFullResyncing || !resyncFromDate">
            <i class="ico sync" :class="{ 'spinning': isFullResyncing }" />
            Lancer la resynchronisation
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de confirmation de suppression totale -->
    <div v-if="showClearAllModal" class="modal-overlay" @click="showClearAllModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Confirmation</h3>
          <button @click="showClearAllModal = false" class="modal-close">
            <i class="ico close" />
          </button>
        </div>
        <div class="modal-body">
          <i class="ico warning-big" />
          <p>Êtes-vous sûr de vouloir supprimer <strong>toutes les données du site</strong> ?</p>
          <p class="warning-text">Cette action est irréversible et supprimera (équivalent à "Clear site data" de Chrome) :</p>
          <ul>
            <li><strong>IndexedDB :</strong> Toutes les propriétés, visites, mutations, dictionnaires</li>
            <li><strong>localStorage</strong> et <strong>sessionStorage</strong></li>
            <li><strong>Cookies</strong></li>
            <li><strong>Cache du service worker</strong></li>
            <li><strong>Service workers enregistrés</strong></li>
          </ul>
          <p class="warning-text">La page sera automatiquement rechargée après la suppression.</p>
        </div>
        <div class="modal-footer">
          <button @click="showClearAllModal = false" class="btn-cancel">
            Annuler
          </button>
          <button @click="clearAllData" class="btn-confirm">
            Confirmer la suppression
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'SyncSidebar',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    isApiAvailable: {
      type: Boolean,
      default: false
    },
    pendingMutations: {
      type: Array,
      default: () => []
    },
    syncStats: {
      type: Object,
      default: () => ({
        total: 0,
        pending: 0,
        synced: 0,
        propertiesWithToSync: 0
      })
    },
    isSyncingData: {
      type: Boolean,
      default: false
    },
    syncProgress: {
      type: Object,
      default: () => ({
        isSyncing: false,
        currentEntity: null,
        method: null,
        currentPage: 0,
        totalPages: 0,
        totalFetched: 0
      })
    }
  },
  computed: {
    entityLabels() {
      return {
        properties: 'Propriétés',
        controls: 'Contrôles',
        teams: 'Équipes',
        users: 'Utilisateurs',
        dictionaries: 'Dictionnaires',
        assistanceMandators: 'Mandants assistance',
        managerMandators: 'Mandants gestionnaire',
        legalOwners: 'Propriétaires légaux',
        interventionPoints: 'Points d\'intervention'
      }
    },
    syncEntityLabel() {
      const entity = this.syncProgress?.currentEntity
      return entity ? (this.entityLabels[entity] || entity) : ''
    },
    /** true quand syncWithExport() télécharge le fichier gzip complet */
    isExportSync() {
      return this.syncProgress?.method === 'export'
    },
    /** Total des modifications locales en attente */
    totalPending() {
      return this.pendingMutations.length + (this.syncStats.propertiesWithToSync || 0)
    }
  },
  data() {
    return {
      isSyncing: false,
      isSmartSyncing: false,
      isFullResyncing: false,
      lastSyncResult: null,
      dbTables: [],
      isRefreshing: false,
      showClearAllModal: false,
      clearingTables: {},
      isIndexedDBSectionOpen: false,
      showResyncModal: false,
      resyncFromDate: moment().subtract(3, 'months').format('YYYY-MM-DD'),
      // Infos de sync locales (properties + controls)
      localSyncInfo: [],
      // État du téléchargement ZIP forcé
      zipDownloading: {
        properties: false,
        controls: false
      }
    }
  },
  methods: {
    closeSidebar() {
      this.$emit('close')
    },

    // ─── Section ① : Mutations locales ─────────────────────────────────────

    async forceSyncNow() {
      if (!this.isApiAvailable || this.isSyncing) return

      this.isSyncing = true
      try {
        const mutationService = (await import('../../services/mutationService')).default
        const result = await mutationService.syncPendingMutations(true)

        this.lastSyncResult = {
          success: result.success,
          syncedCount: result.syncedCount,
          totalCount: result.totalCount,
          errors: result.errors || [],
          timestamp: new Date().toISOString()
        }

        this.$emit('reload-mutations')
      } catch (error) {
        console.error('Erreur lors de la synchronisation forcée:', error)
        this.lastSyncResult = {
          success: false,
          error: error.message,
          timestamp: new Date().toISOString()
        }
      } finally {
        this.isSyncing = false
      }
    },

    // ─── Section ② : Données du serveur ────────────────────────────────────

    /**
     * Déclenche une synchronisation intelligente immédiate (sans attendre le timer périodique).
     */
    async launchSmartSync() {
      if (!this.isApiAvailable || this.isSmartSyncing) return

      this.isSmartSyncing = true
      try {
        const syncService = (await import('../../services/syncService')).default
        const result = await syncService.smartSync()
        this.lastSyncResult = {
          success: result.success !== false,
          timestamp: new Date().toISOString()
        }
        await this.loadLocalSyncInfo()
        await this.refreshTables()
        this.$emit('reload-mutations')
      } catch (error) {
        console.error('[SyncSidebar] Erreur smartSync:', error)
        this.lastSyncResult = {
          success: false,
          error: error.message,
          timestamp: new Date().toISOString()
        }
      } finally {
        this.isSmartSyncing = false
      }
    },

    /**
     * Confirme et déclenche la resynchronisation depuis une date choisie.
     */
    async confirmResync() {
      if (!this.resyncFromDate || this.isFullResyncing) return

      this.showResyncModal = false
      this.isFullResyncing = true

      try {
        await this.$emit('force-full-resync', this.resyncFromDate)
        this.lastSyncResult = {
          success: true,
          syncedCount: undefined,
          timestamp: new Date().toISOString()
        }
      } catch (error) {
        console.error('Erreur lors de la resynchronisation complète:', error)
        this.lastSyncResult = {
          success: false,
          error: error.message,
          timestamp: new Date().toISOString()
        }
      } finally {
        this.isFullResyncing = false
        await this.loadLocalSyncInfo()
        await this.refreshTables()
      }
    },

    // ─── Section ③ : Re-téléchargement ZIP forcé ───────────────────────────

    /**
     * Force le re-téléchargement du ZIP pour une entité ('properties' ou 'controls').
     * Efface l'entrée de sync locale puis déclenche un smartSync.
     */
    async forceZipDownload(entity) {
      if (!this.isApiAvailable || this.zipDownloading[entity]) return

      this.zipDownloading[entity] = true
      try {
        const syncService = (await import('../../services/syncService')).default
        await syncService.forceExportDownload(entity)

        this.lastSyncResult = {
          success: true,
          timestamp: new Date().toISOString()
        }
        await this.loadLocalSyncInfo()
        await this.refreshTables()
        this.$emit('reload-mutations')
      } catch (error) {
        console.error(`[SyncSidebar] Erreur forceZipDownload(${entity}):`, error)
        this.lastSyncResult = {
          success: false,
          error: error.message,
          timestamp: new Date().toISOString()
        }
      } finally {
        this.zipDownloading[entity] = false
      }
    },

    // ─── Informations de sync locales ───────────────────────────────────────

    /**
     * Charge les informations de synchronisation locales depuis IndexedDB
     * pour les entités properties et controls.
     */
    async loadLocalSyncInfo() {
      try {
        const { sync } = await import('../../services/dexieService')
        const syncService = (await import('../../services/syncService')).default

        const displayEntities = ['properties', 'controls']
        const infos = []

        for (const entity of displayEntities) {
          const entry = await sync().get(entity)
          infos.push({
            entity,
            lastSyncDate: entry?.lastSyncDate || null,
            exportGenerationDate: entry?.exportGenerationDate || null,
            status: syncService.calculateSyncStatus(entry?.lastSyncDate || null)
          })
        }

        this.localSyncInfo = infos
      } catch (error) {
        console.error('[SyncSidebar] Erreur chargement sync info:', error)
        this.localSyncInfo = []
      }
    },

    // ─── Utilitaires ────────────────────────────────────────────────────────

    getEntityLabel(entity) {
      const labels = {
        'property': 'Propriété',
        'property_technical_manager': 'Gestionnaire Technique',
        'property_planification': 'Planification Propriété',
        'visit': 'Visite',
        'action': 'Action',
        'control': 'Contrôle',
        'comment': 'Commentaire'
      }
      return labels[entity] || entity
    },

    getMutationTypeLabel(type) {
      const labels = {
        'INSERT': 'Création',
        'UPDATE': 'Modification',
        'DELETE': 'Suppression',
        'property_technical_manager': 'Attribution gestionnaire',
        'property_planification': 'Planification',
        'visit_create': 'Création visite',
        'visit_update': 'Mise à jour visite',
        'control_create': 'Création contrôle',
        'control_update': 'Mise à jour contrôle'
      }
      return labels[type] || type
    },

    getMutationSummary(mutation) {
      const entity = this.getEntityLabel(mutation.entity)
      const operation = this.getMutationTypeLabel(mutation.type)

      switch (mutation.entity) {
        case 'property_technical_manager':
          if (mutation.data && mutation.data.technicalManager) {
            return `${operation} - Gestionnaire: ${mutation.data.technicalManager.fullName || mutation.data.technicalManager.firstName + ' ' + mutation.data.technicalManager.lastName || 'N/A'}`
          }
          return `${operation} de gestionnaire technique`

        case 'property_planification':
          if (mutation.data && mutation.data.planifiedAt) {
            const date = moment(mutation.data.planifiedAt).format('DD/MM/YYYY')
            return `${operation} - Date: ${date}`
          }
          return `${operation} de planification`

        case 'visit':
          return `${operation} de visite`

        case 'control':
          return `${operation} de contrôle`

        case 'action':
          return `${operation} d'action`

        case 'comment':
          return `${operation} de commentaire`

        default:
          return `${operation} - ${entity}`
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return moment(dateString).format('DD/MM/YYYY HH:mm:ss')
    },

    formatDateShort(dateString) {
      if (!dateString) return '—'
      return moment(dateString).format('DD/MM HH:mm')
    },

    // ─── Gestion des tables IndexedDB ───────────────────────────────────────

    async refreshTables() {
      this.isRefreshing = true

      try {
        const { database } = await import('../../services/dexieService')
        const db = database()

        const tableNames = db.tables.map(t => t.name)
        const tables = []

        for (const tableName of tableNames) {
          try {
            const count = await db.table(tableName).count()
            tables.push({ name: tableName, count })
          } catch (error) {
            console.error(`Erreur comptage table ${tableName}:`, error)
            tables.push({ name: tableName, count: 0 })
          }
        }

        this.dbTables = tables.sort((a, b) => a.name.localeCompare(b.name))
      } catch (error) {
        console.error('Erreur chargement tables:', error)
      } finally {
        this.isRefreshing = false
      }
    },

    async clearTable(tableName) {
      this.clearingTables[tableName] = true

      try {
        const { database } = await import('../../services/dexieService')
        const db = database()

        await db.table(tableName).clear()

        const tableIndex = this.dbTables.findIndex(t => t.name === tableName)
        if (tableIndex !== -1) {
          this.dbTables[tableIndex].count = 0
        }

        if (tableName === 'mutations') {
          this.$emit('reload-mutations')
        }

        if (tableName === 'sync') {
          await this.loadLocalSyncInfo()
        }

      } catch (error) {
        console.error(`Erreur vidage table ${tableName}:`, error)
        alert(`Erreur lors du vidage de la table: ${error.message}`)
      } finally {
        this.clearingTables[tableName] = false
      }
    },

    async clearAllData() {
      this.showClearAllModal = false

      try {
        const { database } = await import('../../services/dexieService')
        const db = database()

        await Promise.all(db.tables.map(table => table.clear()))

        localStorage.clear()

        sessionStorage.clear()

        const cookies = document.cookie.split(';')
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i]
          const eqPos = cookie.indexOf('=')
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()
          document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/'
          document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=' + window.location.hostname
        }

        if ('caches' in window) {
          const cacheNames = await caches.keys()
          await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)))
        }

        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations()
          await Promise.all(registrations.map(registration => registration.unregister()))
        }

        alert('Toutes les données du site ont été supprimées avec succès (équivalent à "Clear site data")')
        window.location.reload(true)
      } catch (error) {
        console.error('Erreur suppression toutes données:', error)
        alert(`Erreur lors de la suppression: ${error.message}`)
      }
    },

    async exportMutations() {
      try {
        const { mutations } = await import('../../services/dexieService')
        const allMutations = await mutations().toArray()

        if (allMutations.length === 0) {
          alert('Aucune mutation à exporter')
          return
        }

        const json = JSON.stringify(allMutations, null, 2)
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = `mutations-export-${moment().format('YYYY-MM-DD-HHmmss')}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Erreur export mutations:', error)
        alert(`Erreur lors de l'export: ${error.message}`)
      }
    }
  },

  mounted() {
    this.refreshTables()
    this.loadLocalSyncInfo()
  },

  watch: {
    isOpen(newValue) {
      if (newValue) {
        this.$emit('reload-mutations')
        this.refreshTables()
        this.loadLocalSyncInfo()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import './SyncSidebar.scss';

// ── Sections d'actions ───────────────────────────────────────────────────────

.actions-section {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;

  &.zip-section {
    background: #fff8f0;
    border-left: 3px solid #f59e0b;
  }
}

.actions-section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;

  .section-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    background: #e3f2fd;
    color: #1976d2;
    font-size: 0.8rem;
    font-weight: 700;
    flex-shrink: 0;
  }

  .pending-badge-inline {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.4rem;
    height: 1.4rem;
    padding: 0 0.35rem;
    border-radius: 9999px;
    background: #ef4444;
    color: white;
    font-size: 0.72rem;
    font-weight: 700;
  }
}

// ── Infos stats mutations ─────────────────────────────────────────────────────

.pending-stats {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.85rem;

  .stat-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.4rem 0.75rem;
    background: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e9ecef;

    .stat-label {
      font-size: 0.85rem;
      color: #6b7280;

      i {
        margin-right: 0.3rem;
        color: #9ca3af;
      }
    }

    .stat-value {
      font-size: 1rem;
      font-weight: 700;
      color: #1d4ed8;
    }
  }
}

.no-pending {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #16a34a;
  margin-bottom: 0.85rem;
  padding: 0.4rem 0.75rem;
  background: #f0fdf4;
  border-radius: 6px;
  border: 1px solid #bbf7d0;

  i {
    font-size: 1rem;
  }
}

// ── Boutons d'action ─────────────────────────────────────────────────────────

.action-btn {
  width: 100%;
  padding: 0.7rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.5rem;

  i {
    font-size: 1rem;
    flex-shrink: 0;
  }

  span {
    text-align: left;
    line-height: 1.3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }

  &.primary {
    background: #1d4ed8;
    color: white;

    &:hover:not(:disabled) {
      background: #1e40af;
      transform: translateY(-1px);
      box-shadow: 0 3px 8px rgba(29, 78, 216, 0.35);
    }
  }

  &.secondary {
    background: #e0f2fe;
    color: #0369a1;
    border: 1px solid #bae6fd;

    &:hover:not(:disabled) {
      background: #bae6fd;
      transform: translateY(-1px);
      box-shadow: 0 3px 8px rgba(3, 105, 161, 0.2);
    }
  }

  &.ghost {
    background: transparent;
    color: #4b5563;
    border: 1px solid #d1d5db;

    &:hover:not(:disabled) {
      background: #f9fafb;
      border-color: #9ca3af;
      transform: translateY(-1px);
    }
  }

  &.zip-btn {
    background: #fff7ed;
    color: #b45309;
    border: 1px solid #fed7aa;

    &:hover:not(:disabled) {
      background: #fed7aa;
      transform: translateY(-1px);
      box-shadow: 0 3px 8px rgba(180, 83, 9, 0.25);
    }
  }
}

.action-description {
  margin: 0.4rem 0 0 0;
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.45;
  padding-left: 0.25rem;
}

.section-description {
  margin: 0 0 0.75rem 0;
  font-size: 0.82rem;
  color: #92400e;
  line-height: 1.5;
  padding: 0.5rem 0.75rem;
  background: #fef3c7;
  border-radius: 6px;
  border: 1px solid #fde68a;
}

// ── Tableau des infos de sync ─────────────────────────────────────────────────

.sync-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1px;
  background: #e5e7eb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 0.75rem;
  font-size: 0.78rem;

  .sync-info-row {
    display: contents;

    span {
      background: white;
      padding: 0.4rem 0.5rem;
    }

    &.sync-info-header span {
      background: #f1f5f9;
      font-weight: 700;
      color: #475569;
      text-align: center;
      font-size: 0.72rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    .info-entity {
      font-weight: 600;
      color: #1e293b;
    }

    .info-date {
      color: #64748b;
      text-align: center;
      font-variant-numeric: tabular-nums;
    }

    &.sync-status-fresh .info-date:first-of-type {
      color: #16a34a;
    }
    &.sync-status-outdated .info-date:first-of-type {
      color: #dc2626;
    }
    &.sync-status-never .info-entity {
      color: #9ca3af;
    }
  }
}

.zip-buttons {
  display: flex;
  flex-direction: column;
}
</style>
